import React, { useEffect, useState } from 'react';
import SignupEmailForm from '../components/SignupEmailForm';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import SignupPasswordForm from '../components/SignupPasswordForm';
import OTPVerificationForm from '../components/OTPVerificationForm';
import WaitingPage from '../components/WaitingPage';
import EmailGate from '../components/EmailGate';
import { Grid, CircularProgress, Box, Typography } from '@mui/material';
import appConfig from '../../../config/appConfig';
import { checkEmailStatus } from '../dataHandlers/checkEmailStatusHandler';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { SignupReferralProvider } from '../hooks/useSignupReferral';
import { authPageGridSx } from '../../../styles/authPageLayout';

const SignupPageContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [resolvedState, setResolvedState] = useState(location.state);
  const [hasCheckedBackend, setHasCheckedBackend] = useState(false);
  const { users: u } = useTranslatedMessages();

  useEffect(() => {
    const email = searchParams.get('email') || location.state?.email;
    const hasState = location.state?.stage;

    // If we have state from location, use it
    if (hasState) {
      setResolvedState(location.state);
      return;
    }

    // Skip if no email, already checked, or currently loading
    if (!email || hasCheckedBackend || loading) {
      return;
    }

    // Check backend to determine signup stage
    const checkEmailAndNavigate = async () => {
      setLoading(true);
      setHasCheckedBackend(true);

      try {
        const response = await checkEmailStatus(email);
        const { status, submittedAt } = response;

        const handleStatus = {
          PENDING_ACTIVATION: () => {
            const newState = {
              stage: 'waiting',
              email,
              submittedAt: submittedAt || new Date().toISOString(),
            };
            setResolvedState(newState);
            navigate(location.pathname, { state: newState, replace: true });
          },
          ACTIVE: () => {
            navigate(`/login?email=${encodeURIComponent(email)}`, { replace: true });
          },
          NOT_FOUND: () => {
            const newState = {
              stage: 'signup',
              email,
            };
            setResolvedState(newState);
            navigate(location.pathname, { state: newState, replace: true });
          },
        };

        const handler = handleStatus[status];
        if (handler) {
          handler();
        }
      } catch (error) {
        console.error('Error checking email status:', error);
        setHasCheckedBackend(false); // Allow retry on error
      } finally {
        setLoading(false);
      }
    };

    checkEmailAndNavigate();
  }, [searchParams, location.state, location.pathname, navigate, hasCheckedBackend, loading]);

  const state = resolvedState || location.state;

  // Show loading indicator while checking backend
  if (loading) {
    return (
      <Grid container sx={authPageGridSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {u.signup_page?.checking_account_status ?? 'Checking account status...'}
          </Typography>
        </Box>
      </Grid>
    );
  }

  const getComponent = () => {
    if (!state || !state.stage) {
      return <EmailGate />;
    }

    if (state.stage === 'waiting') {
      return <WaitingPage />;
    }

    if (state.stage === 'verification') {
      return <OTPVerificationForm email={state.email} />;
    }

    if (state.stage === 'signup') {
      return <SignupEmailForm initialEmail={state.email} />;
    }

    if (state.email) {
      return <SignupPasswordForm email={state.email} />;
    }

    return <EmailGate />;
  };

  return (
    <Grid container sx={authPageGridSx}>
      <Grid
        size={{ xs: 0, lg: 7, xl: 6 }}
        sx={{
          display: { sm: 'none', lg: 'flex' },
        }}
      >
        <img
          src={appConfig.brandBackgroundImagePath}
          alt="signup background"
          width="100%"
          style={{ display: 'flex' }}
        />
      </Grid>
      <Grid size={{ xs: 1, sm: 2, md: 3, lg: 1, xl: 2 }} />
      <Grid size={{ xs: 10, sm: 8, md: 6, lg: 3, xl: 3 }}>{getComponent()}</Grid>
      <Grid size={{ xs: 1, sm: 2, md: 3, lg: 1, xl: 1 }} />
    </Grid>
  );
};

const SignupPage = () => (
  <SignupReferralProvider>
    <SignupPageContent />
  </SignupReferralProvider>
);

export default SignupPage;
